import {
  emailRules,
  maxLengthRules,
  minLengthRule,
  passwordRules,
  requiredRules,
} from '@/utils/input-rules.utils';

import { TranslateResult } from 'vue-i18n';

export interface ValidationState {
  valid: boolean;
  touched: boolean;
  error: string | TranslateResult;
}

type TranslateFunction = (
  key: string,
  params?: Record<string, unknown>,
) => TranslateResult;

const MAX_FIELD_LENGTH = 100;
const PW_MIN_LENGTH = 8;
const PW_MAX_LENGTH = 16;

export class FormValidator {
  constructor(private t: TranslateFunction) {}

  validateName(value: string | undefined): ValidationState {
    const valid =
      !!value &&
      requiredRules(value, true) &&
      maxLengthRules(value, MAX_FIELD_LENGTH);
    return {
      valid,
      touched: false,
      error: valid ? '' : this.t('register.name-error'),
    };
  }

  validateSurname(value: string | undefined): ValidationState {
    const valid =
      !!value &&
      requiredRules(value, true) &&
      maxLengthRules(value, MAX_FIELD_LENGTH);
    return {
      valid,
      touched: false,
      error: valid ? '' : this.t('register.surname-error'),
    };
  }

  validateEmail(value: string | undefined): ValidationState {
    const valid =
      !!value && emailRules(value) && maxLengthRules(value, MAX_FIELD_LENGTH);
    return {
      valid,
      touched: false,
      error: valid ? '' : this.t('register.email-error'),
    };
  }

  validatePassword(value: string | undefined): ValidationState {
    const valid = !!value && passwordRules(value);
    return {
      valid,
      touched: false,
      error: valid ? '' : this.t('register.password-error'),
    };
  }

  validateConfirmPassword(
    password: string | undefined,
    confirmPassword: string | undefined,
  ): ValidationState {
    const valid =
      !!confirmPassword &&
      passwordRules(confirmPassword) &&
      password === confirmPassword;
    return {
      valid,
      touched: false,
      error: valid ? '' : this.t('register.confirm-password-error'),
    };
  }

  validatePrivacy(value: boolean): ValidationState {
    const valid = value === true;
    return {
      valid,
      touched: false,
      error: valid ? '' : this.t('register.privacy-error'),
    };
  }

  validateLanguage(value: string | undefined): ValidationState {
    const valid =
      !!value && minLengthRule(value, 2, true) && maxLengthRules(value, 2);
    return {
      valid,
      touched: false,
      error: '',
    };
  }
}

export const passwordChecks = {
  hasValidLength: (pw: string | undefined): boolean => {
    return (
      pw !== undefined &&
      pw.length >= PW_MIN_LENGTH &&
      pw.length <= PW_MAX_LENGTH
    );
  },

  hasDigit: (pw: string | undefined): boolean => {
    return pw !== undefined && /[0-9]/.test(pw);
  },

  hasLowercase: (pw: string | undefined): boolean => {
    return pw !== undefined && /[a-z]/.test(pw);
  },

  hasUppercase: (pw: string | undefined): boolean => {
    return pw !== undefined && /[A-Z]/.test(pw);
  },

  hasSpecialChar: (pw: string | undefined): boolean => {
    return pw !== undefined && /[@#$%^&*_+=[\]{}|\\:',?/`~"();.-]/.test(pw);
  },

  isStrong: (pw: string | undefined): boolean => {
    return (
      passwordChecks.hasValidLength(pw) &&
      passwordChecks.hasDigit(pw) &&
      passwordChecks.hasLowercase(pw) &&
      passwordChecks.hasUppercase(pw) &&
      passwordChecks.hasSpecialChar(pw)
    );
  },
};
